import type { Asset, InventoryAlert, InventoryItem, MaintenanceRecord, PurchaseOrder, StockMovement, Supplier } from "../../services/inventoryService";
import { byNumber, byText, humanise, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * The stores' registers -- stock, purchase orders, suppliers, stock history,
 * alerts, fixed assets and their maintenance -- and the rules the screens
 * explain. Choices mirror apps/inventory/models.py.
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const money = (value?: string | number | null) => (value === null || value === undefined || value === "" ? "" : `UGX ${Math.round(Number(value)).toLocaleString()}`);
const DAY_MS = 86_400_000;
export const daysBetween = (from: string, to: string) => Math.round((Date.parse(to) - Date.parse(from)) / DAY_MS);
export const labelFrom = (pairs: [string, string][], value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? humanise(value);

export const ITEM_TYPES: [string, string][] = [
  ["stationery", "Stationery"],
  ["consumable", "Consumable"],
  ["cleaning", "Cleaning supplies"],
  ["medical", "Medical supplies"],
  ["sports", "Sports equipment"],
  ["equipment", "Equipment"],
  ["furniture", "Furniture"],
  ["technology", "Technology"],
  ["other", "Other"],
];
export const ASSET_TYPES: [string, string][] = [
  ["furniture", "Furniture"],
  ["equipment", "Equipment"],
  ["technology", "Technology"],
  ["vehicle", "Vehicle"],
  ["building", "Building"],
  ["land", "Land"],
  ["other", "Other"],
];
export const CONDITIONS: [string, string][] = [["excellent", "Excellent"], ["good", "Good"], ["fair", "Fair"], ["poor", "Poor"], ["damaged", "Damaged"]];
export const ASSET_STATUSES: [string, string][] = [
  ["active", "In use"],
  ["inactive", "Not in use"],
  ["maintenance", "Being repaired"],
  ["disposed", "Disposed of"],
  ["lost", "Lost"],
  ["stolen", "Stolen"],
];
export const MAINTENANCE_TYPES: [string, string][] = [["preventive", "Routine service"], ["corrective", "Repair"], ["emergency", "Emergency"], ["upgrade", "Upgrade"]];
export const MOVEMENT_TYPES: [string, string][] = [
  ["purchase", "Received"],
  ["adjustment", "Adjusted"],
  ["transfer", "Moved"],
  ["return", "Returned"],
  ["damage", "Damaged"],
  ["theft", "Stolen"],
  ["sale", "Sold"],
  ["other", "Other"],
];
export const RATINGS: [string, string][] = [["1", "Poor"], ["2", "Fair"], ["3", "Good"], ["4", "Very good"], ["5", "Excellent"]];

/** Where an asset may go next; mirrors transition_asset. Disposing of an asset on the ledger is a posting, not a status change. */
export function assetMoves(asset: Pick<Asset, "status" | "is_capitalized">) {
  const moves: Record<string, string[]> = {
    active: ["inactive", "disposed", "lost", "stolen"],
    inactive: ["active", "disposed", "lost", "stolen"],
    maintenance: ["active", "inactive", "disposed", "lost", "stolen"],
    lost: ["active", "disposed"],
    stolen: ["active", "disposed"],
    disposed: [],
  };
  return (moves[asset.status] ?? []).filter((status) => !(status === "disposed" && asset.is_capitalized));
}

// --- Stock -----------------------------------------------------------------------------------

export function stockStatus(item: Pick<InventoryItem, "current_stock" | "minimum_stock" | "reorder_level" | "is_active">) {
  if (!item.is_active) return { label: "Not stocked", dot: "bg-gray-400" };
  if (item.current_stock <= 0) return { label: "Out of stock", dot: "bg-red-500" };
  if (item.current_stock <= item.minimum_stock) return { label: "Low", dot: "bg-red-500" };
  if (item.current_stock <= item.reorder_level) return { label: "Reorder now", dot: "bg-amber-500" };
  return { label: "In stock", dot: "bg-emerald-500" };
}

export const needsReorder = (item: Pick<InventoryItem, "current_stock" | "reorder_level" | "is_active">) => item.is_active && item.current_stock <= item.reorder_level;
export const stockValue = (item: Pick<InventoryItem, "current_stock" | "unit_cost">) => item.current_stock * Number(item.unit_cost);

/** How much to order to bring an item back up to its maximum (at least one). */
export const reorderQuantity = (item: Pick<InventoryItem, "current_stock" | "maximum_stock" | "reorder_level">) =>
  Math.max(1, (item.maximum_stock || item.reorder_level * 2) - item.current_stock);

export const itemConfig: RegisterConfig<InventoryItem> = {
  search: (row) => [row.name, row.sku, row.category_name, row.storage_location, row.barcode].join(" "),
  views: [
    { key: "stocked", label: "Stocked", match: (row) => row.is_active },
    { key: "reorder", label: "Reorder now", match: needsReorder },
    { key: "out", label: "Out of stock", match: (row) => row.is_active && row.current_stock <= 0 },
    { key: "inactive", label: "Not stocked", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  category: { label: "Category", value: (row) => row.category_name },
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<InventoryItem>((row) => row.name) },
    { key: "urgent", label: "Lowest against reorder level", compare: byNumber<InventoryItem>((row) => row.current_stock - row.reorder_level) },
    { key: "-value", label: "Highest stock value", compare: reversed(byNumber<InventoryItem>(stockValue)) },
    { key: "location", label: "Store location", compare: byText<InventoryItem>((row) => `${row.storage_location ?? "~"} ${row.name}`) },
  ],
};

export const movementConfig: RegisterConfig<StockMovement> = {
  search: (row) => [row.item_name, row.item_sku, row.reference_number, row.reason, row.performed_by_name].join(" "),
  views: [
    { key: "all", label: "All" },
    { key: "in", label: "In", match: (row) => row.quantity > 0 },
    { key: "out", label: "Out", match: (row) => row.quantity < 0 },
    { key: "received", label: "Received on orders", match: (row) => row.movement_type === "purchase" },
  ],
  date: { label: "Date", value: (row) => row.created_at },
  category: { label: "Item", value: (row) => row.item_name },
  sorts: [
    { key: "-when", label: "Newest first", compare: reversed(byText<StockMovement>((row) => `${row.created_at}${String(row.id).padStart(8, "0")}`)) },
    { key: "when", label: "Oldest first", compare: byText<StockMovement>((row) => `${row.created_at}${String(row.id).padStart(8, "0")}`) },
    { key: "item", label: "Item (A–Z)", compare: byText<StockMovement>((row) => row.item_name) },
  ],
};

export const movementLabel = (row: Pick<StockMovement, "movement_type" | "reference_type" | "quantity">) => {
  if (row.reference_type === "opening_stock") return "Opening stock";
  if (row.movement_type === "adjustment") return row.quantity < 0 ? "Issued or written off" : "Added";
  return labelFrom(MOVEMENT_TYPES, row.movement_type);
};

// --- Purchase orders -------------------------------------------------------------------------

export function orderStatus(order: Pick<PurchaseOrder, "status" | "expected_delivery_date">, today: string) {
  switch (order.status) {
    case "draft":
      return { label: "Draft", dot: "bg-gray-400" };
    case "pending":
      return { label: "Waiting for approval", dot: "bg-amber-500" };
    case "approved":
      return { label: "Approved, not sent", dot: "bg-indigo-500" };
    case "ordered":
      return order.expected_delivery_date && order.expected_delivery_date < today ? { label: "Delivery overdue", dot: "bg-red-500" } : { label: "With the supplier", dot: "bg-sky-500" };
    case "received":
      return { label: "Received", dot: "bg-emerald-500" };
    default:
      return { label: "Cancelled", dot: "bg-gray-400" };
  }
}

export const isOpenOrder = (order: Pick<PurchaseOrder, "status">) => !["received", "cancelled"].includes(order.status);

export const orderConfig: RegisterConfig<PurchaseOrder> = {
  search: (row) => [row.order_number, row.supplier_name, row.requested_by_name, row.notes, ...row.items.map((line) => line.item_name)].join(" "),
  views: [
    { key: "open", label: "Open", match: isOpenOrder },
    { key: "drafts", label: "Drafts", match: (row) => row.status === "draft" },
    { key: "approval", label: "Waiting for approval", match: (row) => row.status === "pending" },
    { key: "supplier", label: "With the supplier", match: (row) => row.status === "approved" || row.status === "ordered" },
    { key: "received", label: "Received", match: (row) => row.status === "received" },
    { key: "all", label: "All" },
  ],
  date: { label: "Order date", value: (row) => row.order_date },
  category: { label: "Supplier", value: (row) => row.supplier_name },
  sorts: [
    { key: "-date", label: "Newest first", compare: reversed(byText<PurchaseOrder>((row) => `${row.order_date}${String(row.id).padStart(8, "0")}`)) },
    { key: "delivery", label: "Due soonest", compare: byText<PurchaseOrder>((row) => (isOpenOrder(row) ? row.expected_delivery_date || "9998" : "9999")) },
    { key: "-total", label: "Largest first", compare: reversed(byNumber<PurchaseOrder>((row) => row.total_amount)) },
  ],
};

export interface OrderLine {
  item: string;
  quantity: string;
  unit_cost: string;
}
export const lineTotal = (line: OrderLine) => (Number(line.quantity) || 0) * (Number(line.unit_cost) || 0);
export const orderTotal = (lines: OrderLine[], tax: string | number) => lines.reduce((sum, line) => sum + lineTotal(line), 0) + (Number(tax) || 0);

/** A draft order's lines for every stocked item at or below its reorder level. */
export const reorderLines = (items: InventoryItem[]): OrderLine[] =>
  items.filter(needsReorder).map((item) => ({ item: String(item.id), quantity: String(reorderQuantity(item)), unit_cost: item.unit_cost }));

// --- Suppliers -------------------------------------------------------------------------------

export const supplierConfig: RegisterConfig<Supplier> = {
  search: (row) => [row.name, row.contact_person, row.phone, row.email, row.address].join(" "),
  views: [
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "inactive", label: "Inactive", match: (row) => !row.is_active },
    { key: "all", label: "All" },
  ],
  sorts: [
    { key: "name", label: "Name (A–Z)", compare: byText<Supplier>((row) => row.name) },
    { key: "-rating", label: "Best rated", compare: reversed(byNumber<Supplier>((row) => row.rating ?? 0)) },
  ],
};

// --- Alerts ----------------------------------------------------------------------------------

export const alertDot = (priority: string) => (priority === "critical" ? "bg-red-600" : priority === "high" ? "bg-red-500" : priority === "medium" ? "bg-amber-500" : "bg-gray-400");

export const alertConfig: RegisterConfig<InventoryAlert> = {
  search: (row) => [row.title, row.message, row.item_name, row.asset_name].join(" "),
  views: [
    { key: "new", label: "Not yet seen to", match: (row) => !row.is_read },
    { key: "seen", label: "Seen to", match: (row) => row.is_read },
    { key: "all", label: "All" },
  ],
  category: { label: "Kind", value: (row) => humanise(row.alert_type) },
  sorts: [
    { key: "severity", label: "Most serious first", compare: (a, b) => ["critical", "high", "medium", "low"].indexOf(a.priority) - ["critical", "high", "medium", "low"].indexOf(b.priority) || b.created_at.localeCompare(a.created_at) },
    { key: "-when", label: "Newest first", compare: reversed(byText<InventoryAlert>((row) => row.created_at)) },
  ],
};

// --- Assets ----------------------------------------------------------------------------------

export const WARRANTY_WARNING_DAYS = 60;
export const isGone = (asset: Pick<Asset, "status">) => ["disposed", "lost", "stolen"].includes(asset.status);

export function assetStatus(asset: Pick<Asset, "status">) {
  const dot: Record<string, string> = { active: "bg-emerald-500", inactive: "bg-gray-400", maintenance: "bg-amber-500", disposed: "bg-gray-400", lost: "bg-red-500", stolen: "bg-red-500" };
  return { label: labelFrom(ASSET_STATUSES, asset.status), dot: dot[asset.status] ?? "bg-gray-400" };
}

/** Whether the asset is in the books, for the CFO's list. */
export function ledgerStatus(asset: Pick<Asset, "is_capitalized" | "status" | "disposal_date">) {
  if (asset.disposal_date) return { label: "Written off", dot: "bg-gray-400" };
  if (asset.is_capitalized) return { label: "On the ledger", dot: "bg-emerald-500" };
  if (isGone(asset)) return { label: "Never posted", dot: "bg-gray-300" };
  return { label: "Not on the ledger", dot: "bg-amber-500" };
}

export function warranty(asset: Pick<Asset, "warranty_expiry">, today: string) {
  if (!asset.warranty_expiry) return null;
  const days = daysBetween(today, asset.warranty_expiry);
  return { expiry: asset.warranty_expiry, days, expired: days < 0, soon: days >= 0 && days <= WARRANTY_WARNING_DAYS };
}

export const assetConfig = (today: string): RegisterConfig<Asset> => ({
  search: (row) => [row.asset_tag, row.name, row.serial_number, row.manufacturer, row.location, row.category_name, row.assigned_to_name].join(" "),
  views: [
    { key: "held", label: "Held", match: (row) => !isGone(row) },
    { key: "repair", label: "Being repaired", match: (row) => row.status === "maintenance" },
    { key: "unposted", label: "Not on the ledger", match: (row) => !row.is_capitalized && !isGone(row) },
    { key: "service", label: "Service due", match: (row) => !isGone(row) && Boolean(row.next_maintenance_date) && daysBetween(today, row.next_maintenance_date!) <= 30 },
    { key: "gone", label: "Disposed, lost or stolen", match: isGone },
    { key: "all", label: "All" },
  ],
  category: { label: "Type", value: (row) => labelFrom(ASSET_TYPES, row.asset_type) },
  sorts: [
    { key: "tag", label: "Tag", compare: byText<Asset>((row) => row.asset_tag) },
    { key: "name", label: "Name (A–Z)", compare: byText<Asset>((row) => row.name) },
    { key: "-value", label: "Highest book value", compare: reversed(byNumber<Asset>((row) => row.net_book_value)) },
    { key: "location", label: "Location", compare: byText<Asset>((row) => `${row.location ?? "~"} ${row.asset_tag}`) },
  ],
});

// --- Asset maintenance -----------------------------------------------------------------------

export function maintenanceStatus(row: Pick<MaintenanceRecord, "status" | "scheduled_date">, today: string) {
  switch (row.status) {
    case "scheduled":
      return row.scheduled_date && row.scheduled_date < today ? { label: "Overdue", dot: "bg-red-500" } : { label: "Booked", dot: "bg-indigo-500" };
    case "in_progress":
      return { label: "Being done", dot: "bg-sky-500" };
    case "completed":
      return { label: "Done", dot: "bg-emerald-500" };
    default:
      return { label: "Cancelled", dot: "bg-gray-400" };
  }
}

export const maintenanceConfig = (today: string): RegisterConfig<MaintenanceRecord> => ({
  search: (row) => [row.asset_name, row.asset_tag, row.title, row.description, row.performed_by, row.work_performed].join(" "),
  views: [
    { key: "open", label: "Open", match: (row) => row.status === "scheduled" || row.status === "in_progress" },
    { key: "overdue", label: "Overdue", match: (row) => row.status === "scheduled" && Boolean(row.scheduled_date) && row.scheduled_date! < today },
    { key: "done", label: "Done", match: (row) => row.status === "completed" },
    { key: "cancelled", label: "Cancelled", match: (row) => row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Booked for", value: (row) => row.scheduled_date },
  category: { label: "Kind", value: (row) => labelFrom(MAINTENANCE_TYPES, row.maintenance_type) },
  sorts: [
    { key: "when", label: "Soonest first", compare: byText<MaintenanceRecord>((row) => row.scheduled_date ?? "9999") },
    { key: "-when", label: "Latest first", compare: reversed(byText<MaintenanceRecord>((row) => row.performed_date ?? row.scheduled_date ?? "")) },
    { key: "-cost", label: "Most expensive", compare: reversed(byNumber<MaintenanceRecord>((row) => row.total_cost)) },
  ],
});
